import {
  EmptyState,
  NotStartedIcon,
  PageHeader,
  Button,
  PermissionIcon,
} from '@lmc/ui-kit'
import { FC, useEffect, useState } from 'react'
import { Text } from '@lmc/ui-kit/src/general'
import { useNavigate } from 'react-router-dom'
import { DeviceStatus, UserRole } from '@lmc/ts-common'
import { PreferencesBanner } from './components/PreferencesBanner'
import styled from 'styled-components'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { dacConstants } from 'locale/deviceAccessControl'
import { useDevices } from './hooks'
import { DevicesTable } from './components/DevicesTable'
import useDevicesAccess from 'components/AccessControl/features/useDevicesAccess'
import { Spinner } from '../../../../components/generic'
import { TablePaginationConfig } from 'antd'
import { tableParamsDefaultState } from './utils'
import { TableParams } from './types'
import { useRoleAccess } from 'components/AccessControl'
import { StatusSwitch } from './components/StatusSwitch'
import { HashLink } from 'react-router-hash-link'
import { DEVICES_SETTINGS_PAGE_WITH_HASH } from './types'

const HELP_DISABLED =
  'https://support.logmycare.co.uk/en/articles/8010663-how-do-i-set-up-device-access-control'

const HELP_ENABLED =
  'https://support.logmycare.co.uk/en/articles/8010670-how-do-i-approve-a-new-device-for-my-care-home'

export const DeviceAccessScreen: FC = () => {
  const navigate = useNavigate()

  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const hasDeviceAccessControl = useDevicesAccess()

  const hasRolePermission = useRoleAccess(UserRole.careHomeAdmin)

  const [status, setStatus] = useState<DeviceStatus | undefined>(
    DeviceStatus.approved
  )

  const [tableParams, setTableParams] = useState<TableParams>(
    tableParamsDefaultState()
  )

  const { devices: devicesAllStatuses, isLoading: isLoadingAllStatuses } =
    useDevices(undefined, hasRolePermission)

  const hasAnyStatusDevices =
    !isLoadingAllStatuses && !!devicesAllStatuses?.length

  const { devices, isLoading, count, refetch } = useDevices(
    status,
    hasRolePermission && hasDeviceAccessControl && hasAnyStatusDevices,
    tableParams.pagination?.current,
	tableParams.pagination?.pageSize
  )

  useEffect(() => {
    if (!tableParams?.pagination?.total) {
      setTableParams(tableParamsDefaultState(count))
    }
  }, [count])

  const hasSpecificStatusDevices = !isLoading && !!devices?.length

  const handlePaginationChange = (pagination: TablePaginationConfig) => {
    setTableParams({
      pagination,
    })
  }

  const handleNavigateDacSigns = () => navigate('/organisation/devices/signs')

  if (isLoadingAllStatuses) {
    return <Spinner />
  }

  if (!hasRolePermission) {
    return (
      <Wrapper>
        <PageHeader
          title={t(dacConstants.DEVICES)}
          moreInfoLink={HELP_DISABLED}
        />
        <EmptyState
          title={t(dacConstants.PSST)}
          description={t(dacConstants.NO_PERMISSIONS)}
          icon={<PermissionIcon />}
        />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <PageHeader
        title={t(dacConstants.DEVICES)}
        moreInfoLink={hasDeviceAccessControl ? HELP_ENABLED : HELP_DISABLED}
      >
        {hasAnyStatusDevices && (
          <Button
            data-cy="btn-view-all-signs"
            variant="text"
            onClick={handleNavigateDacSigns}
          >
            {t(dacConstants.VIEW_ALL)}
          </Button>
        )}
      </PageHeader>
      {!hasDeviceAccessControl && <PreferencesBanner />}
      {hasAnyStatusDevices && (
        <ContentWrapper>
          {hasDeviceAccessControl && (
            <StatusSwitch
              isLoading={isLoading}
              hasSpecificStatusDevices={hasSpecificStatusDevices}
              setStatus={setStatus}
              setTableParams={setTableParams}
            />
          )}
          {(hasSpecificStatusDevices ||
            (hasAnyStatusDevices && !hasDeviceAccessControl)) && (
            <TableWrapper>
              <DevicesTable
                devices={hasDeviceAccessControl ? devices : devicesAllStatuses}
                handleTableChange={handlePaginationChange}
                tableParams={tableParams}
                hasDeviceAccessControl={hasDeviceAccessControl}
                navigate={navigate}
                refetch={refetch}
              />
            </TableWrapper>
          )}
          {isLoading && <Spinner />}
        </ContentWrapper>
      )}
      {!hasAnyStatusDevices && (
        <EmptyState
          title={t(dacConstants.THIS_IS_WHERE_YOU)}
          description={
            <EmptyStateWrapper>
              <Text>{t(dacConstants.MAKE_SURE_YOU)}</Text>
              <HashLink to={DEVICES_SETTINGS_PAGE_WITH_HASH} smooth>
                <StyledNavigateButton variant="text" data-cy="btn-preferences">
                  {t(dacConstants.PREFERENCES)}
                </StyledNavigateButton>
              </HashLink>
              <Text>{t(dacConstants.TO_START_APPROVING)}</Text>
            </EmptyStateWrapper>
          }
          icon={<NotStartedIcon />}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  margin: 0 auto;
  padding: 24px;
  width: 100%;
  max-width: 1080px;
`

const TableWrapper = styled.div`
  overflow-y: auto;
`

const ContentWrapper = styled.div`
  margin-top: 10px;
`

const EmptyStateWrapper = styled.div`
  display: flex;
`

const StyledNavigateButton = styled(Button)`
  margin-left: 4px;
  margin-right: 4px;
`

export default DeviceAccessScreen
